/**
 * ┌────────────────────────────────────────────────────────┐
 * │ ChargeAfter Consumer Financing - Apply Now Modal       │
 * └────────────────────────────────────────────────────────┘
 * Self-contained script to load the ChargeAfter SDK and
 * trigger the application modal.
 */

(() => {
  'use strict';

  // Read configuration injected from theme.liquid settings
  const config = window.ChargeAfterConfig || {};
  const CA_CONFIG = {
    publicKey: config.publicKey || '',
    environment: config.environment || 'sandbox',
  };

  // Prevent script from running if the key is missing
  if (!CA_CONFIG.publicKey) {
    console.warn('ChargeAfter: Public Key is missing in Theme Settings.');
    return;
  }

  // Look for specific button class (directly or on descendants), or any link pointing to #apply-now
  const BUTTON_SELECTORS =
    '.chargeafter-apply-btn, .chargeafter-apply-btn .btn, .chargeafter-apply-btn button, .chargeafter-apply-btn a, a[href="#apply-now"]';

  const setupApplyButtons = () => {
    const buttons = document.querySelectorAll(BUTTON_SELECTORS);
    if (!buttons.length) return;

    buttons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();

        // Check if the SDK loaded and the present method is available
        if (typeof window.ChargeAfter !== 'undefined' && window.ChargeAfter.payments?.present) {
          const presentConfig = {
            preferences: { language: 'En' },
            currency: 'USD',
          };

          if (CA_CONFIG.storeId) presentConfig.storeId = CA_CONFIG.storeId;

          window.ChargeAfter.payments.present('apply', presentConfig);
        } else {
          // Fallback Logic: SDK is missing or blocked
          console.error(
            'ChargeAfter Error: SDK failed to load or initialize. It may be blocked by the browser.'
          );

          const fallbackUrl = button.getAttribute('href');

          // If the button is a real link, send them there safely
          if (fallbackUrl && fallbackUrl !== '#' && !fallbackUrl.startsWith('#')) {
            window.location.href = fallbackUrl;
          } else {
            // Otherwise, alert the user so they aren't left clicking a dead button
            alert(
              'Our financing application is currently unavailable, possibly due to a browser ad-blocker. Please disable it and refresh, or contact support.'
            );
          }
        }
      });
    });
  };

  const loadChargeAfterSDK = () => {
    // Only load the heavy SDK if an apply button actually exists on this page
    if (!document.querySelector(BUTTON_SELECTORS)) return;

    // If the Shopify App already loaded the SDK, just bind the buttons
    if (typeof window.ChargeAfter !== 'undefined') {
      setupApplyButtons();
      return;
    }

    // Otherwise, inject the script manually
    const sdkUrl =
      CA_CONFIG.environment === 'production'
        ? 'https://cdn.chargeafter.com/web/v2/chargeafter.min.js'
        : 'https://cdn-sandbox.ca-dev.co/web/v2/chargeafter.min.js';

    const script = document.createElement('script');
    script.src = sdkUrl;
    script.async = true;

    // Handle successful script download
    script.onload = () => {
      if (window.ChargeAfter && window.ChargeAfter.init) {
        window.ChargeAfter.init({
          apiKey: CA_CONFIG.publicKey,
          environment: CA_CONFIG.environment,
          onLoaded: setupApplyButtons,
        });
      } else {
        // Error Logic: Script loaded, but init is missing (API change or partial block)
        console.error(
          'ChargeAfter Error: The SDK script loaded, but window.ChargeAfter.init is missing.'
        );
        setupApplyButtons(); // Bind buttons anyway so the fallback alert triggers on click
      }
    };

    // Error Logic: Script completely failed to download (Network error or Ad Blocker)
    script.onerror = () => {
      console.error(
        'ChargeAfter Error: Failed to load the SDK script completely (network error or blocked).'
      );
      setupApplyButtons(); // Bind buttons anyway so the fallback alert triggers on click
    };

    document.head.appendChild(script);
  };

  // Initialize on load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', loadChargeAfterSDK);
  } else {
    loadChargeAfterSDK();
  }
})();
